/**
 * Itineraries Domain
 * Types, store, handlers, and service methods for itinerary management.
 * Uses on-demand subscription model - clients only receive data for routes they're viewing.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';
import { sendGatedCommand, type GatedRequest } from './command-gate';
import { showErrorToast } from './toasts';
import { handlePriceChangeUndone, handlePriceChangePending, useSegmentsStore } from './segments';
import i18n from '../i18n/config';
import { matchConnection, sendToMatch } from '../services/nakama/match-send';

// ==================== TYPES ====================

/** Seat class constants */
export const SeatClass = {
  ECON: 0,
  BUSINESS: 1,
  FIRST: 2,
} as const;

export type SeatClassType = typeof SeatClass[keyof typeof SeatClass];

export const RoutePriceStrategy = {
  VALUE: 'value',
  BALANCED: 'balanced',
  PREMIUM: 'premium',
} as const;

export type RoutePriceStrategyType = typeof RoutePriceStrategy[keyof typeof RoutePriceStrategy];

/** Each strategy's name, and the note that says how it moves fares: keys written whole. */
export const STRATEGY_WORDS: Record<RoutePriceStrategyType, { name: string; note: string }> = {
  value: { name: 'globe.pair.strategies.value', note: 'globe.pair.strategyNotes.value' },
  balanced: { name: 'globe.pair.strategies.balanced', note: 'globe.pair.strategyNotes.balanced' },
  premium: { name: 'globe.pair.strategies.premium', note: 'globe.pair.strategyNotes.premium' },
};

export interface RoutePricingPolicyView {
  airport_id_origin: number;
  airport_id_dest: number;
  route_price_strategy: RoutePriceStrategyType;
  summary?: string;
}

/**
 * Itinerary represents a complete travel route between two endpoints.
 * Mirrors the server's Itinerary struct in itineraries.go.
 * Each itinerary contains data for ALL 3 seat classes indexed by [ECON=0, BUSINESS=1, FIRST=2].
 */
export interface Itinerary {
  /** Unique identifier for this itinerary */
  itinerary_id: number;
  /** Team ID that owns this itinerary */
  team_id: number;
  /** Flight number (e.g., "CT001" for direct, "CT0001" for connecting) */
  flight_number: string;
  /** Lower airport ID endpoint */
  airport_id_origin: number;
  /** Higher airport ID endpoint */
  airport_id_dest: number;
  /** Total travel time in minutes (one-way) - same for all classes */
  total_time: number;
  /** Per-connection layover times in minutes [stop1, stop2]. Zero for direct flights. */
  connection_times: [number, number];
  /** Ticket price (round-trip) per class [ECON, BUSINESS, FIRST] */
  ticket_price: [number, number, number];
  /** Whether this itinerary is manually priced across all classes */
  ticket_price_manual: boolean;
  /** Comfort rating per class (max 100) [ECON, BUSINESS, FIRST] */
  comfort_score: [number, number, number];
  /** Counter of tickets sold per class [ECON, BUSINESS, FIRST] */
  ticket_sales: [number, number, number];
  /** Whether at least one segment has seats for each class [ECON, BUSINESS, FIRST] */
  class_available: [boolean, boolean, boolean];
  /** Number of segments (1-3) */
  num_segments: number;
  /** Segment IDs in route order */
  segment_ids: number[];
}

interface AirportDestinationTeamSummary {
  bestSegmentCount: number | null;
  fastestTime: number | null;
  flightCount: number;
}

export interface AirportDestinationSummary {
  destinationAirportId: number;
  totalFlightCount: number;
  teamCount: number;
  overallFastestTime: number | null;
  teamSummaries: Record<number, AirportDestinationTeamSummary>;
}

interface AirportFlightsCacheEntry {
  itineraries: Itinerary[];
  fetchedAtMs: number;
}

interface RouteGroupingBuckets {
  direct: number[];
  oneStop: number[];
  twoStop: number[];
}

interface RouteGroupingIndex {
  byStopCount: RouteGroupingBuckets;
  byDestinationAndStop: Record<number, RouteGroupingBuckets>;
  stopCountByItineraryId: Record<number, number>;
  destinationByItineraryId: Record<number, number>;
  directItineraryIdBySegmentId: Record<number, number>;
  referencedSegmentIds: number[];
}

const AIRPORT_FLIGHTS_CACHE_MAX_AIRPORTS = 15;
const AIRPORT_FLIGHTS_CACHE_TTL_MS = 60_000;
const AIRPORT_FLIGHTS_SUBSCRIBE_DEBOUNCE_MS = 180;
const airportFlightsCache = new Map<number, AirportFlightsCacheEntry>();

let pendingAirportSubscribeTimer: ReturnType<typeof setTimeout> | null = null;
let pendingAirportSubscribeToken = 0;
let activeServerAirportSubscriptionId: number | null = null;

function cloneItinerary(itinerary: Itinerary): Itinerary {
  return {
    ...itinerary,
    ticket_price: [...itinerary.ticket_price] as [number, number, number],
    ticket_price_manual: itinerary.ticket_price_manual,
    comfort_score: [...itinerary.comfort_score] as [number, number, number],
    ticket_sales: [...itinerary.ticket_sales] as [number, number, number],
    class_available: [...itinerary.class_available] as [boolean, boolean, boolean],
    segment_ids: [...itinerary.segment_ids],
  };
}

function cloneItineraries(itineraries: Itinerary[]): Itinerary[] {
  return itineraries.map(cloneItinerary);
}

function areTripletsEqual<T extends number | boolean>(
  a: [T, T, T] | undefined,
  b: [T, T, T] | undefined
): boolean {
  if (!a || !b) return false;
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

function areNumberArraysEqual(a: number[] | undefined, b: number[] | undefined): boolean {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function areItinerariesEquivalent(current: Itinerary, incoming: Itinerary): boolean {
  return (
    current.itinerary_id === incoming.itinerary_id &&
    current.team_id === incoming.team_id &&
    current.flight_number === incoming.flight_number &&
    current.airport_id_origin === incoming.airport_id_origin &&
    current.airport_id_dest === incoming.airport_id_dest &&
    current.total_time === incoming.total_time &&
    current.num_segments === incoming.num_segments &&
    areTripletsEqual(current.ticket_price, incoming.ticket_price) &&
    current.ticket_price_manual === incoming.ticket_price_manual &&
    areTripletsEqual(current.comfort_score, incoming.comfort_score) &&
    areTripletsEqual(current.ticket_sales, incoming.ticket_sales) &&
    areTripletsEqual(current.class_available, incoming.class_available) &&
    areNumberArraysEqual(current.segment_ids, incoming.segment_ids) &&
    areNumberArraysEqual(current.connection_times, incoming.connection_times)
  );
}

/**
 * The key routePolicies is indexed by: the pair ordered ascending, so both
 * directions of a route resolve to one policy. Exported because the pricing
 * hook must build the SAME key the handler writes -- two copies of this format
 * would mean every route silently reads as BALANCED the day one drifts.
 */
export function getOrderedRoutePolicyKey(airportId1: number, airportId2: number): string {
  const originId = Math.min(airportId1, airportId2);
  const destId = Math.max(airportId1, airportId2);
  return `${originId}:${destId}`;
}

function buildRoutePolicyMap(routePolicies: RoutePricingPolicyView[]): Record<string, RoutePricingPolicyView> {
  const nextPolicies: Record<string, RoutePricingPolicyView> = {};
  for (const policy of routePolicies) {
    if (!policy) continue;
    const key = getOrderedRoutePolicyKey(policy.airport_id_origin, policy.airport_id_dest);
    nextPolicies[key] = { ...policy };
  }
  return nextPolicies;
}

function areFlightMapsEquivalent(
  currentFlights: Record<number, Itinerary>,
  incomingFlights: Record<number, Itinerary>
): boolean {
  const currentIds = Object.keys(currentFlights);
  const incomingIds = Object.keys(incomingFlights);
  if (currentIds.length !== incomingIds.length) return false;

  for (const itineraryIdRaw of currentIds) {
    const itineraryId = Number(itineraryIdRaw);
    const current = currentFlights[itineraryId];
    const incoming = incomingFlights[itineraryId];
    if (!current || !incoming || !areItinerariesEquivalent(current, incoming)) {
      return false;
    }
  }

  return true;
}

function getItinerarySegmentCount(itinerary: Itinerary): number {
  if (itinerary.num_segments !== undefined && itinerary.num_segments !== null && itinerary.num_segments > 0) {
    return itinerary.num_segments;
  }

  let nonZeroSegmentCount = 0;
  for (const segmentId of itinerary.segment_ids ?? []) {
    if (segmentId !== 0) {
      nonZeroSegmentCount += 1;
    }
  }
  return nonZeroSegmentCount > 0 ? nonZeroSegmentCount : 1;
}

function itineraryTouchesAirport(itinerary: Itinerary, airportId: number): boolean {
  return itinerary.airport_id_origin === airportId || itinerary.airport_id_dest === airportId;
}

function getDestinationAirportId(itinerary: Itinerary, subscribedAirportId: number): number {
  return itinerary.airport_id_origin === subscribedAirportId
    ? itinerary.airport_id_dest
    : itinerary.airport_id_origin;
}

function buildDestinationSummaryForItineraryIds(
  destinationAirportId: number,
  flights: Record<number, Itinerary>,
  itineraryIds: number[]
): AirportDestinationSummary | null {
  const totalFlightKeys = new Set<string>();
  const teamIds = new Set<number>();
  let overallFastestTime: number | null = null;
  const teamData = new Map<
    number,
    {
      bestSegmentCount: number | null;
      fastestTime: number | null;
      flightKeys: Set<string>;
    }
  >();

  for (const itineraryId of itineraryIds) {
    const itinerary = flights[itineraryId];
    if (!itinerary) continue;

    const teamId = itinerary.team_id;
    const segmentCount = getItinerarySegmentCount(itinerary);
    const flightKey = `${teamId}-${itinerary.flight_number || itinerary.itinerary_id}`;

    totalFlightKeys.add(flightKey);
    if (teamId) {
      teamIds.add(teamId);
    }
    if (overallFastestTime === null || itinerary.total_time < overallFastestTime) {
      overallFastestTime = itinerary.total_time;
    }

    let entry = teamData.get(teamId);
    if (!entry) {
      entry = {
        bestSegmentCount: null,
        fastestTime: null,
        flightKeys: new Set<string>(),
      };
      teamData.set(teamId, entry);
    }

    entry.flightKeys.add(flightKey);
    if (entry.bestSegmentCount === null || segmentCount < entry.bestSegmentCount) {
      entry.bestSegmentCount = segmentCount;
    }
    if (entry.fastestTime === null || itinerary.total_time < entry.fastestTime) {
      entry.fastestTime = itinerary.total_time;
    }
  }

  if (totalFlightKeys.size === 0) {
    return null;
  }

  const teamSummaries: Record<number, AirportDestinationTeamSummary> = {};
  teamData.forEach((entry, teamId) => {
    teamSummaries[teamId] = {
      bestSegmentCount: entry.bestSegmentCount,
      fastestTime: entry.fastestTime,
      flightCount: entry.flightKeys.size,
    };
  });

  return {
    destinationAirportId,
    totalFlightCount: totalFlightKeys.size,
    teamCount: teamIds.size,
    overallFastestTime,
    teamSummaries,
  };
}

function addItineraryIdToDestinationIndex(
  itineraryIdsByDestination: Record<number, number[]>,
  destinationAirportId: number,
  itineraryId: number
): Record<number, number[]> {
  const existingIds = itineraryIdsByDestination[destinationAirportId] || [];
  if (existingIds.includes(itineraryId)) {
    return itineraryIdsByDestination;
  }

  return {
    ...itineraryIdsByDestination,
    [destinationAirportId]: [...existingIds, itineraryId],
  };
}

function removeItineraryIdFromDestinationIndex(
  itineraryIdsByDestination: Record<number, number[]>,
  destinationAirportId: number,
  itineraryId: number
): Record<number, number[]> {
  const existingIds = itineraryIdsByDestination[destinationAirportId];
  if (!existingIds) {
    return itineraryIdsByDestination;
  }

  const nextIds = existingIds.filter((id) => id !== itineraryId);
  if (nextIds.length === existingIds.length) {
    return itineraryIdsByDestination;
  }

  const nextIndex = { ...itineraryIdsByDestination };
  if (nextIds.length === 0) {
    delete nextIndex[destinationAirportId];
  } else {
    nextIndex[destinationAirportId] = nextIds;
  }
  return nextIndex;
}

function buildAirportDestinationIndexes(
  flights: Record<number, Itinerary>,
  subscribedAirportId: number | null
): {
  connectedDestinationIds: Set<number>;
  destinationSummaries: Record<number, AirportDestinationSummary>;
  itineraryIdsByDestination: Record<number, number[]>;
} {
  if (subscribedAirportId === null) {
    return {
      connectedDestinationIds: new Set<number>(),
      destinationSummaries: {},
      itineraryIdsByDestination: {},
    };
  }

  const itineraryIdsByDestination: Record<number, number[]> = {};

  for (const itinerary of Object.values(flights)) {
    if (!itineraryTouchesAirport(itinerary, subscribedAirportId)) {
      continue;
    }

    const destinationAirportId = getDestinationAirportId(itinerary, subscribedAirportId);
    if (!itineraryIdsByDestination[destinationAirportId]) {
      itineraryIdsByDestination[destinationAirportId] = [];
    }
    itineraryIdsByDestination[destinationAirportId].push(itinerary.itinerary_id);
  }

  const connectedDestinationIds = new Set<number>();
  const destinationSummaries: Record<number, AirportDestinationSummary> = {};

  for (const [destinationAirportIdRaw, itineraryIds] of Object.entries(itineraryIdsByDestination)) {
    const destinationAirportId = Number(destinationAirportIdRaw);
    const summary = buildDestinationSummaryForItineraryIds(destinationAirportId, flights, itineraryIds);
    if (!summary) continue;
    connectedDestinationIds.add(destinationAirportId);
    destinationSummaries[destinationAirportId] = summary;
  }

  return {
    connectedDestinationIds,
    destinationSummaries,
    itineraryIdsByDestination,
  };
}

function createRouteGroupingBuckets(): RouteGroupingBuckets {
  return {
    direct: [],
    oneStop: [],
    twoStop: [],
  };
}

function createEmptyRouteGroupingIndex(): RouteGroupingIndex {
  return {
    byStopCount: createRouteGroupingBuckets(),
    byDestinationAndStop: {},
    stopCountByItineraryId: {},
    destinationByItineraryId: {},
    directItineraryIdBySegmentId: {},
    referencedSegmentIds: [],
  };
}

function buildRouteGroupingIndex(
  flights: Record<number, Itinerary>,
  subscribedAirportId: number | null
): RouteGroupingIndex {
  if (subscribedAirportId === null) {
    return createEmptyRouteGroupingIndex();
  }

  const byStopCount = createRouteGroupingBuckets();
  const byDestinationAndStop: Record<number, RouteGroupingBuckets> = {};
  const stopCountByItineraryId: Record<number, number> = {};
  const destinationByItineraryId: Record<number, number> = {};
  const directItineraryIdBySegmentId: Record<number, number> = {};
  const referencedSegmentIds = new Set<number>();

  for (const itinerary of Object.values(flights)) {
    if (!itineraryTouchesAirport(itinerary, subscribedAirportId)) continue;

    const segmentCount = getItinerarySegmentCount(itinerary);
    const stopCount = Math.max(0, segmentCount - 1);
    const destinationAirportId = getDestinationAirportId(itinerary, subscribedAirportId);

    stopCountByItineraryId[itinerary.itinerary_id] = stopCount;
    destinationByItineraryId[itinerary.itinerary_id] = destinationAirportId;

    const bucketKey = stopCount === 0 ? 'direct' : stopCount === 1 ? 'oneStop' : 'twoStop';
    byStopCount[bucketKey].push(itinerary.itinerary_id);

    if (!byDestinationAndStop[destinationAirportId]) {
      byDestinationAndStop[destinationAirportId] = createRouteGroupingBuckets();
    }
    byDestinationAndStop[destinationAirportId][bucketKey].push(itinerary.itinerary_id);

    let directSegmentId: number | null = null;
    for (const segmentId of itinerary.segment_ids ?? []) {
      if (segmentId === 0) continue;
      referencedSegmentIds.add(segmentId);
      if (directSegmentId === null) {
        directSegmentId = segmentId;
      }
    }

    if (bucketKey === 'direct' && directSegmentId !== null) {
      directItineraryIdBySegmentId[directSegmentId] = itinerary.itinerary_id;
    }
  }

  // Index direct itineraries included by the server for connecting leg navigation.
  // These don't touch the subscribed airport but their segments are used by connecting
  // itineraries that do (e.g., MEM-MSY direct when subscribed to OKC with OKC-MEM-MSY).
  for (const itinerary of Object.values(flights)) {
    if (itineraryTouchesAirport(itinerary, subscribedAirportId)) continue;
    const segmentCount = getItinerarySegmentCount(itinerary);
    if (segmentCount !== 1) continue;
    const segmentId = (itinerary.segment_ids ?? []).find(id => id !== 0);
    if (!segmentId) continue;
    if (directItineraryIdBySegmentId[segmentId] !== undefined) continue;
    directItineraryIdBySegmentId[segmentId] = itinerary.itinerary_id;
    referencedSegmentIds.add(segmentId);
  }

  return {
    byStopCount,
    byDestinationAndStop,
    stopCountByItineraryId,
    destinationByItineraryId,
    directItineraryIdBySegmentId,
    referencedSegmentIds: Array.from(referencedSegmentIds),
  };
}

function upsertAirportFlightsCache(airportId: number, itineraries: Itinerary[], fetchedAtMs = Date.now()): void {
  airportFlightsCache.delete(airportId);
  airportFlightsCache.set(airportId, {
    itineraries: cloneItineraries(itineraries),
    fetchedAtMs,
  });

  while (airportFlightsCache.size > AIRPORT_FLIGHTS_CACHE_MAX_AIRPORTS) {
    const lruAirportId = airportFlightsCache.keys().next().value as number | undefined;
    if (lruAirportId === undefined) break;
    airportFlightsCache.delete(lruAirportId);
  }
}

function getCachedAirportFlights(airportId: number): { itineraries: Itinerary[]; isStale: boolean } | null {
  const entry = airportFlightsCache.get(airportId);
  if (!entry) return null;

  const nowMs = Date.now();
  const isStale = nowMs - entry.fetchedAtMs > AIRPORT_FLIGHTS_CACHE_TTL_MS;

  // Touch for LRU
  airportFlightsCache.delete(airportId);
  airportFlightsCache.set(airportId, entry);

  return {
    itineraries: cloneItineraries(entry.itineraries),
    isStale,
  };
}

function cancelPendingAirportSubscribe(): void {
  pendingAirportSubscribeToken += 1;
  if (pendingAirportSubscribeTimer !== null) {
    clearTimeout(pendingAirportSubscribeTimer);
    pendingAirportSubscribeTimer = null;
  }
}

/**
 * City pair for subscription tracking
 */
// ==================== HELPER FUNCTIONS ====================

/**
 * Formats travel time in hours and minutes
 */
export function formatTravelTime(minutes: number): string {
  if (!Number.isFinite(minutes)) return '—';
  const totalMinutes = Math.max(0, Math.round(minutes));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h${String(mins).padStart(2, '0')}m`;
}

// ==================== HANDLERS ====================

/**
 * Handles price update success
 */
export function handleUpdateItineraryPriceSuccess(data: {
  type: string;
  itinerary: Itinerary;
}) {
  console.log('[Itineraries] Price update successful for itinerary:', data.itinerary.itinerary_id);

  // Update the store with the server-confirmed itinerary data
  const store = useAirportFlightsStore.getState();
  store.updateFlight(data.itinerary);
  endPriceChange(true);
}

/**
 * A refused fare: a toast says so, with the server's reason. The flights stay
 * as they were — the list is not the place for a passing refusal.
 */
export function handleUpdateItineraryPriceFailed(data: { type: string; error: string }) {
  console.error('[Itineraries] Price update failed:', data.error);
  endPriceChange(false);
  showErrorToast(i18n.t('globe.pair.fareFailed'), data.error);
}

export function handleUpdateRoutePricingPolicySuccess(data: {
  type: string;
  route_policy: RoutePricingPolicyView;
  itineraries: Itinerary[];
}) {
  const store = useAirportFlightsStore.getState();
  useAirportFlightsStore.setState({ strategyPendingKey: null });
  if (data.route_policy) {
    store.upsertRoutePolicy(data.route_policy);
  }
  for (const itinerary of data.itineraries || []) {
    store.updateFlight(itinerary);
  }
}

/** A refused price strategy: it is no longer on its way, and a toast says why. */
export function handleUpdateRoutePricingPolicyFailed(data: { type: string; error: string }) {
  console.error('[Itineraries] Route pricing policy update failed:', data.error);
  useAirportFlightsStore.setState({ strategyPendingKey: null });
  showErrorToast(i18n.t('globe.pair.strategyFailed'), data.error);
}

export function handleResetItineraryPriceToAutoSuccess(data: {
  type: string;
  itinerary: Itinerary;
}) {
  const store = useAirportFlightsStore.getState();
  store.updateFlight(data.itinerary);
  endPriceChange(true);
}

/** A refused return to Auto: a toast, and the flights as they were. */
export function handleResetItineraryPriceToAutoFailed(data: { type: string; error: string }) {
  console.error('[Itineraries] Reset itinerary price to auto failed:', data.error);
  endPriceChange(false);
  showErrorToast(i18n.t('globe.pair.autoFailed'), data.error);
}

/** A flight's prices on their way: a fare in one class, or every fare back to Auto. */
interface PriceChange {
  itineraryId: number;
  kind: 'fare' | 'auto';
  /** The class and the fare sent, for a new fare. */
  seatClass?: SeatClassType;
  price?: number;
  /** The segments this change marked as waiting for the next update. */
  marked: number[];
}

/**
 * Marks the itinerary's segments as waiting for the next update — its load
 * factors will not say the new price's effect until then — and returns the
 * ones it marked, which were not waiting already.
 */
function markItineraryPending(itineraryId: number): number[] {
  const itinerary = useAirportFlightsStore.getState().flights[itineraryId];
  const { pendingModifications } = useSegmentsStore.getState();
  const marked = (itinerary?.segment_ids ?? []).filter((id) => id !== 0 && !pendingModifications.has(id));
  handlePriceChangePending(marked);
  return marked;
}

/**
 * The price change on its way is over. Taken — or perhaps taken, its answer
 * lost with a dropped socket — its marks stay until the next update, which
 * says what it did; refused or never sent, nothing changed, so they go.
 */
function endPriceChange(taken: boolean): void {
  const change = useAirportFlightsStore.getState().priceChange;
  if (!change) return;
  useAirportFlightsStore.setState({ priceChange: null });
  if (!taken) handlePriceChangeUndone(change.marked);
}

/** Sends a price change for a flight, which is on its way until it is answered or dropped. */
async function sendPriceChange(change: Omit<PriceChange, 'marked'>, opCode: GatedRequest, message: object): Promise<void> {
  useAirportFlightsStore.setState({ priceChange: { ...change, marked: markItineraryPending(change.itineraryId) } });
  await sendGatedCommand(opCode, JSON.stringify(message), {
    // An answer lost with the socket may have been a change taken; one turned
    // away, or never sent, changed nothing.
    endWait: (lost) => endPriceChange(lost),
    notSent: ({ error }) =>
      change.kind === 'fare'
        ? handleUpdateItineraryPriceFailed({ type: 'send', error })
        : handleResetItineraryPriceToAutoFailed({ type: 'send', error }),
  });
}

// ==================== SERVICE METHODS ====================

/**
 * Updates the ticket price for an itinerary for a specific seat class.
 * Only works for itineraries owned by the player's team.
 * @param itineraryId - The itinerary ID to update
 * @param classIndex - The seat class (0=ECON, 1=BUSINESS, 2=FIRST)
 * @param newPrice - The new ticket price
 */
export async function updateItineraryPrice(itineraryId: number, classIndex: SeatClassType, newPrice: number): Promise<void> {
  const message = {
    type: MessageType.MessageTypeUpdateItineraryPrice,
    itinerary_id: itineraryId,
    class: classIndex,
    new_price: newPrice,
  };

  console.log('[Itineraries] Updating price for itinerary:', itineraryId, 'class:', classIndex, 'to', newPrice);

  await sendPriceChange({ itineraryId, kind: 'fare', seatClass: classIndex, price: newPrice }, OpCode.OpCodeUpdateItineraryPrice, message);
}

/**
 * Sets a pair's price strategy for your team. It is on its way until the
 * server answers — `strategyPendingKey` names the pair — or until the command
 * gate drops it for another command still in flight, or the socket drops
 * with it.
 */
export async function updateRoutePricingPolicy(
  originId: number,
  destId: number,
  routePriceStrategy: RoutePriceStrategyType,
): Promise<void> {
  const message = {
    type: MessageType.MessageTypeUpdateRoutePricingPolicy,
    origin_id: originId,
    dest_id: destId,
    route_price_strategy: routePriceStrategy,
  };

  useAirportFlightsStore.setState({ strategyPendingKey: getOrderedRoutePolicyKey(originId, destId) });
  await sendGatedCommand(OpCode.OpCodeUpdateRoutePricingPolicy, JSON.stringify(message), {
    endWait: () => useAirportFlightsStore.setState({ strategyPendingKey: null }),
    notSent: ({ error }) => handleUpdateRoutePricingPolicyFailed({ type: 'send', error }),
  });
}

/** Hands a flight's fares back to its route's strategy: on its way until answered or dropped. */
export async function resetItineraryPriceToAuto(itineraryId: number): Promise<void> {
  const message = {
    type: MessageType.MessageTypeResetItineraryPriceToAuto,
    itinerary_id: itineraryId,
  };

  await sendPriceChange({ itineraryId, kind: 'auto' }, OpCode.OpCodeResetItineraryPriceToAuto, message);
}

// ==================== AIRPORT FLIGHTS STORE ====================
// Separate store for viewing all flights from/to a single airport

interface AirportFlightsState {
  /** Airport ID currently expected from server flight-state messages. */
  requestedAirportId: number | null;

  /** Currently subscribed airport ID (null if not subscribed) */
  subscribedAirportId: number | null;

  /** All itineraries for the airport (itinerary_id -> Itinerary) */
  flights: Record<number, Itinerary>;

  /** Route-level pricing policies for the active airport keyed by ordered OD pair */
  routePolicies: Record<string, RoutePricingPolicyView>;

  /** Set of destination airport IDs that have at least one flight connection */
  connectedDestinationIds: Set<number>;

  /** Pre-aggregated destination summaries keyed by destination airport ID */
  destinationSummaries: Record<number, AirportDestinationSummary>;

  /** Destination -> itinerary IDs index for incremental summary/filter updates */
  itineraryIdsByDestination: Record<number, number[]>;

  /** Pre-grouped itinerary indexes for routes panel rendering */
  routeGrouping: RouteGroupingIndex;

  /** Currently selected destination ID for detail view (null if viewing list) */
  selectedDestinationId: number | null;

  /** Whether the user is currently viewing the destinations section of the airport panel */
  isInDestinationsView: boolean;

  /** Whether to show only the current player's team routes (vs all teams) */
  showOursOnly: boolean;

  /** The pair (its route policy key) whose price strategy is on its way to the server, if any. */
  strategyPendingKey: string | null;

  /**
   * A flight's prices on their way: a new fare in one class — held where it
   * was let go until the server answers — or all of them handed back to Auto.
   */
  priceChange: PriceChange | null;

  /** Loading state */
  isLoading: boolean;

  /** Error message if any */
  error: string | null;

  /** Actions */
  setSubscribedAirportId: (airportId: number | null) => void;
  updateFlight: (itinerary: Itinerary) => void;
  removeFlight: (itineraryId: number) => void;
  setFlights: (itineraries: Itinerary[]) => void;
  setRoutePolicies: (routePolicies: RoutePricingPolicyView[]) => void;
  upsertRoutePolicy: (routePolicy: RoutePricingPolicyView) => void;
  setSelectedDestinationId: (destId: number | null) => void;
  setIsInDestinationsView: (isInView: boolean) => void;
  setShowOursOnly: (showOurs: boolean) => void;
  setError: (error: string | null) => void;
  /** Everything, gone: the connection was reset, so nothing on its way will be answered. */
  reset: () => void;
  /**
   * The dock let go of its airport: its flights go, but a change on its way
   * stays until the server answers — a refused fare still has its marks to
   * take back.
   */
  clearAirport: () => void;
  /** Atomically switch to a new airport - prevents "show all" flash during transitions */
  switchToAirport: (airportId: number) => void;
}

const airportFlightsInitialState = {
  requestedAirportId: null,
  subscribedAirportId: null,
  flights: {},
  routePolicies: {},
  connectedDestinationIds: new Set<number>(),
  destinationSummaries: {},
  itineraryIdsByDestination: {},
  routeGrouping: createEmptyRouteGroupingIndex(),
  selectedDestinationId: null,
  isInDestinationsView: false,
  showOursOnly: false,
  strategyPendingKey: null,
  priceChange: null,
  isLoading: false,
  error: null,
};

export const useAirportFlightsStore = create<AirportFlightsState>()(
  devtools(
    (set, get) => ({
      ...airportFlightsInitialState,
      
      setSubscribedAirportId: (airportId) => set({ subscribedAirportId: airportId }),
      
      updateFlight: (itinerary) => {
        const {
          subscribedAirportId,
          flights,
          connectedDestinationIds,
          destinationSummaries,
          itineraryIdsByDestination,
        } = get();
        
        // Only update if we're subscribed to this airport
        if (subscribedAirportId === null) return;
        
        // Check if flight involves subscribed airport
        if (!itineraryTouchesAirport(itinerary, subscribedAirportId)) {
          // Accept updates for itineraries already in store — the server included them
          // for connecting leg navigation even though they don't touch this airport.
          if (!flights[itinerary.itinerary_id]) return;
          const nextFlights = { ...flights, [itinerary.itinerary_id]: itinerary };
          set({
            flights: nextFlights,
            routeGrouping: buildRouteGroupingIndex(nextFlights, subscribedAirportId),
          });
          return;
        }

        const previousItinerary = flights[itinerary.itinerary_id];
        const nextFlights = {
          ...flights,
          [itinerary.itinerary_id]: itinerary,
        };

        let nextItineraryIdsByDestination = itineraryIdsByDestination;
        const destinationsToRecompute = new Set<number>();

        if (previousItinerary && itineraryTouchesAirport(previousItinerary, subscribedAirportId)) {
          const previousDestinationId = getDestinationAirportId(previousItinerary, subscribedAirportId);
          nextItineraryIdsByDestination = removeItineraryIdFromDestinationIndex(
            nextItineraryIdsByDestination,
            previousDestinationId,
            itinerary.itinerary_id
          );
          destinationsToRecompute.add(previousDestinationId);
        }

        const nextDestinationId = getDestinationAirportId(itinerary, subscribedAirportId);
        nextItineraryIdsByDestination = addItineraryIdToDestinationIndex(
          nextItineraryIdsByDestination,
          nextDestinationId,
          itinerary.itinerary_id
        );
        destinationsToRecompute.add(nextDestinationId);

        const nextConnectedDestinationIds = new Set(connectedDestinationIds);
        const nextDestinationSummaries = { ...destinationSummaries };

        for (const destinationId of destinationsToRecompute) {
          const destinationItineraryIds = nextItineraryIdsByDestination[destinationId] || [];
          const summary = buildDestinationSummaryForItineraryIds(destinationId, nextFlights, destinationItineraryIds);
          if (summary) {
            nextDestinationSummaries[destinationId] = summary;
            nextConnectedDestinationIds.add(destinationId);
          } else {
            delete nextDestinationSummaries[destinationId];
            nextConnectedDestinationIds.delete(destinationId);
          }
        }

        const nextRouteGrouping = buildRouteGroupingIndex(nextFlights, subscribedAirportId);

        set({
          flights: nextFlights,
          connectedDestinationIds: nextConnectedDestinationIds,
          destinationSummaries: nextDestinationSummaries,
          itineraryIdsByDestination: nextItineraryIdsByDestination,
          routeGrouping: nextRouteGrouping,
        });
      },
      
      removeFlight: (itineraryId) => {
        const {
          flights,
          subscribedAirportId,
          connectedDestinationIds,
          destinationSummaries,
          itineraryIdsByDestination,
        } = get();
        const removedItinerary = flights[itineraryId];
        if (!removedItinerary) return;

        const newFlights = { ...flights };
        delete newFlights[itineraryId];

        if (subscribedAirportId === null || !itineraryTouchesAirport(removedItinerary, subscribedAirportId)) {
          set({
            flights: newFlights,
            routeGrouping: buildRouteGroupingIndex(newFlights, subscribedAirportId),
          });
          return;
        }

        const removedDestinationId = getDestinationAirportId(removedItinerary, subscribedAirportId);
        const nextItineraryIdsByDestination = removeItineraryIdFromDestinationIndex(
          itineraryIdsByDestination,
          removedDestinationId,
          itineraryId
        );
        const remainingDestinationItineraryIds = nextItineraryIdsByDestination[removedDestinationId] || [];
        const updatedSummary = buildDestinationSummaryForItineraryIds(
          removedDestinationId,
          newFlights,
          remainingDestinationItineraryIds
        );
        const nextConnectedDestinationIds = new Set(connectedDestinationIds);
        const nextDestinationSummaries = { ...destinationSummaries };
        if (updatedSummary) {
          nextDestinationSummaries[removedDestinationId] = updatedSummary;
          nextConnectedDestinationIds.add(removedDestinationId);
        } else {
          delete nextDestinationSummaries[removedDestinationId];
          nextConnectedDestinationIds.delete(removedDestinationId);
        }

        const nextRouteGrouping = buildRouteGroupingIndex(newFlights, subscribedAirportId);

        set({
          flights: newFlights,
          connectedDestinationIds: nextConnectedDestinationIds,
          destinationSummaries: nextDestinationSummaries,
          itineraryIdsByDestination: nextItineraryIdsByDestination,
          routeGrouping: nextRouteGrouping,
        });
      },
      
      setRoutePolicies: (routePolicies) => set({
        routePolicies: buildRoutePolicyMap(routePolicies),
      }),

      upsertRoutePolicy: (routePolicy) => set((state) => ({
        routePolicies: {
          ...state.routePolicies,
          [getOrderedRoutePolicyKey(routePolicy.airport_id_origin, routePolicy.airport_id_dest)]: { ...routePolicy },
        },
      })),
      
      setFlights: (itineraries) => {
        const { subscribedAirportId, flights } = get();
        const flightsMap: Record<number, Itinerary> = {};
        
        for (const itin of itineraries) {
          flightsMap[itin.itinerary_id] = itin;
        }

        // Common switch path: cache hydration is followed by an identical server state.
        // Skip rebuilding all indexes when payload content is unchanged.
        if (areFlightMapsEquivalent(flights, flightsMap)) {
          set({
            isLoading: false,
            error: null,
          });
          return;
        }

        const { connectedDestinationIds, destinationSummaries, itineraryIdsByDestination } = buildAirportDestinationIndexes(
          flightsMap,
          subscribedAirportId
        );
        const routeGrouping = buildRouteGroupingIndex(flightsMap, subscribedAirportId);
        set({
          flights: flightsMap,
          connectedDestinationIds,
          destinationSummaries,
          itineraryIdsByDestination,
          routeGrouping,
          isLoading: false,
          error: null,
        });
      },
      
      setSelectedDestinationId: (destId) => set({ selectedDestinationId: destId }),

      setIsInDestinationsView: (isInView) => set({ isInDestinationsView: isInView }),

      setShowOursOnly: (showOurs) => set({ showOursOnly: showOurs }),

      setError: (error) => set({ error, isLoading: false }),
      
      reset: () => {
        // What the module keeps beside the store goes too: the next match's
        // airports must not open on this one's cached flights, a subscribe on
        // its way must not go to the next match, and no subscription is held
        // on a server this client has left.
        airportFlightsCache.clear();
        cancelPendingAirportSubscribe();
        activeServerAirportSubscriptionId = null;
        set(airportFlightsInitialState);
      },

      clearAirport: () =>
        set((state) => ({
          ...airportFlightsInitialState,
          strategyPendingKey: state.strategyPendingKey,
          priceChange: state.priceChange,
        })),
      
      switchToAirport: (airportId) => set((state) => ({
        requestedAirportId: airportId,
        // Keep subscribedAirportId set to avoid "show all segments" flash during transition
        subscribedAirportId: airportId,
        flights: {},
        routePolicies: {},
        connectedDestinationIds: new Set<number>(),
        destinationSummaries: {},
        itineraryIdsByDestination: {},
        routeGrouping: createEmptyRouteGroupingIndex(),
        selectedDestinationId: null,
        // Preserve view state when switching airports within destinations view
        isInDestinationsView: state.isInDestinationsView,
        showOursOnly: state.showOursOnly,
        strategyPendingKey: state.strategyPendingKey,
        priceChange: state.priceChange,
        isLoading: true,
        error: null,
      })),
    }),
    { name: 'airport-flights-store' }
  )
);

// ==================== AIRPORT FLIGHTS HANDLERS ====================

/**
 * Handles airport flights state from server
 */
export function handleAirportFlightsState(data: {
  type: string;
  airport_id: number;
  itineraries: Itinerary[];
  route_policies?: RoutePricingPolicyView[];
}) {
  const store = useAirportFlightsStore.getState();
  if (store.requestedAirportId === null || data.airport_id !== store.requestedAirportId) {
    return;
  }
  store.setSubscribedAirportId(data.airport_id);
  store.setRoutePolicies(data.route_policies || []);
  store.setFlights(data.itineraries || []);
  upsertAirportFlightsCache(data.airport_id, data.itineraries || []);
}

/**
 * Handles a single airport flight update from server (new or modified)
 */
export function handleAirportFlightUpdate(data: {
  type: string;
  itinerary: Itinerary
}) {
  const store = useAirportFlightsStore.getState();
  store.updateFlight(data.itinerary);

  const activeAirportId = store.requestedAirportId;
  if (activeAirportId !== null) {
    upsertAirportFlightsCache(activeAirportId, Object.values(useAirportFlightsStore.getState().flights));
  }
}

/**
 * Handles airport flight removal notification from server
 */
export function handleAirportFlightRemoved(data: {
  type: string;
  itinerary_id: number;
  airport_id: number;
}) {
  const store = useAirportFlightsStore.getState();
  if (store.requestedAirportId === null || data.airport_id !== store.requestedAirportId) {
    return;
  }
  store.removeFlight(data.itinerary_id);
  upsertAirportFlightsCache(data.airport_id, Object.values(useAirportFlightsStore.getState().flights));
}

// ==================== AIRPORT FLIGHTS SERVICE ====================

/**
 * The city panel's Routes are open, or not: the globe draws a city's
 * connecting routes only while they are.
 */
export function showCityRoutes(open: boolean): void {
  useAirportFlightsStore.getState().setIsInDestinationsView(open);
}

/** Back out of one destination's flights to the city's destinations. */
export function closeDestination(): void {
  useAirportFlightsStore.getState().setSelectedDestinationId(null);
}

/** Open one destination's flights from the city's destinations. */
export function openDestination(destinationId: number): void {
  useAirportFlightsStore.getState().setSelectedDestinationId(destinationId);
}

/** Show only your airline's routes and flights, or everyone's — on the panel and the globe alike. */
export function showOnlyOurs(ours: boolean): void {
  useAirportFlightsStore.getState().setShowOursOnly(ours);
}

/**
 * The rejoin's snapshot has landed. While the player was gone the server may
 * have dropped them from the open airport's subscribers — a broadcast to an
 * airport forgets a subscriber who is not online
 * (getAirportFlightSubscriberPresences, itineraries.go) — and nothing else
 * would ask again, so the airport the dock still shows is asked for afresh.
 */
export function resubscribeAirportFlightsAfterRejoin(): void {
  activeServerAirportSubscriptionId = null;
  const airportId = useAirportFlightsStore.getState().requestedAirportId;
  if (airportId !== null) void subscribeToAirportFlights(airportId);
}

/** The panels' dock is empty: the airport's flights go, and a change on its way stays. */
export function clearAirportFlights(): void {
  useAirportFlightsStore.getState().clearAirport();
}

/**
 * Subscribes to airport flights.
 * Sends subscribe message to server and sets loading state.
 */
export async function subscribeToAirportFlights(airportId: number): Promise<void> {
  if (!matchConnection()) {
    console.warn('[AirportFlights] Not subscribing: not connected to the match');
    return;
  }
  
  const store = useAirportFlightsStore.getState();
  const cached = getCachedAirportFlights(airportId);

  // Atomically switch to the new airport to avoid "show all segments" flash.
  store.switchToAirport(airportId);

  // Serve cached airport data immediately (stale-while-revalidate).
  // We still subscribe below to refresh from server and receive live updates.
  if (cached !== null) {
    store.setFlights(cached.itineraries);
  }

  cancelPendingAirportSubscribe();
  const requestToken = pendingAirportSubscribeToken;

  pendingAirportSubscribeTimer = setTimeout(() => {
    pendingAirportSubscribeTimer = null;
    void (async () => {
      if (requestToken !== pendingAirportSubscribeToken) return;
      if (useAirportFlightsStore.getState().requestedAirportId !== airportId) return;

      if (activeServerAirportSubscriptionId !== null && activeServerAirportSubscriptionId !== airportId) {
        const unsubscribeMessage = {
          type: MessageType.MessageTypeUnsubscribeAirportFlights,
          airport_id: activeServerAirportSubscriptionId,
        };

        if (await sendToMatch(OpCode.OpCodeUnsubscribeAirportFlights, unsubscribeMessage, "the previous airport's unsubscribe")) {
          activeServerAirportSubscriptionId = null;
        }
      }

      const subscribeMessage = {
        type: MessageType.MessageTypeSubscribeAirportFlights,
        airport_id: airportId,
      };

      if (await sendToMatch(OpCode.OpCodeSubscribeAirportFlights, subscribeMessage, "an airport's subscribe")) {
        activeServerAirportSubscriptionId = airportId;
      } else {
        useAirportFlightsStore.getState().setError('Failed to subscribe to airport flights');
      }
    })();
  }, AIRPORT_FLIGHTS_SUBSCRIBE_DEBOUNCE_MS);
}
